import { useState } from 'react';
import { useEditMode } from '@/contexts/EditModeContext';
import { useAuth } from '@/contexts/AuthContext';
import { Pencil, Check, X, Plus, Trash2, GripVertical, FileText, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

// Utility functions for media detection
const getYoutubeVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const getYoutubeThumbnail = (url: string, quality: 'max' | 'sd' | 'hq' = 'max'): string | null => {
  const videoId = getYoutubeVideoId(url);
  if (!videoId) return null;
  
  const qualityMap = {
    max: 'maxresdefault',
    sd: 'sddefault',
    hq: 'hqdefault'
  };
  
  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
};

const getMediaType = (url: string | any): 'image' | 'video' | 'pdf' => {
  // Handle non-string URLs (might be objects)
  const urlStr = typeof url === 'string' ? url : (url?.url || '');
  if (!urlStr) return 'image';
  
  if (urlStr.includes('youtube.com') || urlStr.includes('youtu.be') || getYoutubeVideoId(urlStr)) {
    return 'video';
  }
  if (urlStr.toLowerCase().endsWith('.pdf')) {
    return 'pdf';
  }
  return 'image';
};

interface EditableGalleryProps {
  images: (string | { url: string })[];
  onSave: (images: string[]) => Promise<void>;
  onImageSelect: (image: string) => void;
  selectedImage: string;
  className?: string;
}

export const EditableGallery = ({
  images,
  onSave,
  onImageSelect,
  selectedImage,
  className,
}: EditableGalleryProps) => {
  const { isEditMode } = useEditMode();
  const { isEditor } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  
  // Normalize images to strings
  const normalizedImages = images.map(img => typeof img === 'string' ? img : img.url);
  const [editImages, setEditImages] = useState<string[]>(normalizedImages);
  const [isSaving, setIsSaving] = useState(false);

  if (!isEditor || !isEditMode) {
    return (
      <div className={cn('grid grid-cols-4 gap-3', className)}>
        {normalizedImages.slice(0, 4).map((url, index) => {
          const mediaType = getMediaType(url);
          const thumbnail = mediaType === 'video' ? getYoutubeThumbnail(url) : url;
          
          return (
            <button
              key={index}
              onClick={() => onImageSelect(url)}
              className={`group relative aspect-video bg-card rounded-lg overflow-hidden border-2 transition-all shadow-sm hover:shadow-md ${
                selectedImage === url
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-border hover:border-primary'
              }`}
            >
              {mediaType === 'pdf' ? (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <FileText className="h-12 w-12 text-muted-foreground" />
                </div>
              ) : (
                <img
                  src={thumbnail || url}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    if (target.src.includes('maxresdefault')) {
                      target.src = target.src.replace('maxresdefault', 'sddefault');
                    } else if (target.src.includes('sddefault')) {
                      target.src = target.src.replace('sddefault', 'hqdefault');
                    }
                  }}
                />
              )}

              {mediaType === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <PlayCircle className="h-10 w-10 text-white drop-shadow-lg" />
                </div>
              )}
              <div
                className={`absolute inset-0 transition-colors ${
                  selectedImage === url
                    ? 'bg-black/0'
                    : 'bg-black/0 group-hover:bg-black/10'
                }`}
              />
            </button>
          );
        })}
      </div>
    );
  }

  const handleSave = async () => {
    const filteredImages = editImages.filter((img) => img.trim() !== '');
    setIsSaving(true);
    try {
      await onSave(filteredImages);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditImages(normalizedImages);
    setIsEditing(false);
  };

  const handleAdd = () => {
    setEditImages([...editImages, '']);
  };

  const handleRemove = (index: number) => {
    setEditImages(editImages.filter((_, i) => i !== index));
  };

  const handleUpdate = (index: number, value: string) => {
    const newImages = [...editImages];
    newImages[index] = value;
    setEditImages(newImages);
  };

  if (isEditing) {
    return (
      <div className="space-y-4 p-4 border-2 border-orange-300 rounded-lg bg-orange-50/10">
        <div>
          <h3 className="font-semibold text-lg">Edit Gallery</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Add images, YouTube videos, or PDFs. Videos will show thumbnails with play buttons.
          </p>
        </div>
        {editImages.map((image, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-2">
                <Label>Media URL {index + 1}</Label>
                <Input
                  type="url"
                  value={image}
                  onChange={(e) => handleUpdate(index, e.target.value)}
                  placeholder="https://example.com/image.jpg or https://youtube.com/watch?v=..."
                />
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-8"
                onClick={() => handleRemove(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {image && (
              <div className="bg-muted rounded-lg p-4 flex items-center justify-center h-32">
                {(() => {
                  const mediaType = getMediaType(image);
                  if (mediaType === 'pdf') {
                    return <FileText className="h-16 w-16 text-muted-foreground" />;
                  }
                  if (mediaType === 'video') {
                    const thumbnail = getYoutubeThumbnail(image);
                    return thumbnail ? (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <img
                          src={thumbnail}
                          alt={`Video preview ${index + 1}`}
                          className="max-h-full max-w-full object-contain"
                        />
                        <PlayCircle className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-8 text-white" />
                      </div>
                    ) : (
                      <PlayCircle className="h-16 w-16 text-muted-foreground" />
                    );
                  }
                  return (
                    <img
                      src={image}
                      alt={`Preview ${index + 1}`}
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => {
                        const target = e.currentTarget;
                        if (target.src.includes('maxresdefault')) {
                          target.src = target.src.replace('maxresdefault', 'sddefault');
                        } else if (target.src.includes('sddefault')) {
                          target.src = target.src.replace('sddefault', 'hqdefault');
                        } else {
                          target.src = 'https://cdn3.iconfinder.com/data/icons/communication-social-media-1/24/account_profile_user_contact_person_avatar_placeholder-512.png';
                        }
                      }}
                    />
                  );
                })()}
              </div>
            )}
          </div>
        ))}
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleAdd} className="flex-1">
            <Plus className="h-4 w-4 mr-1" />
            Add Media
          </Button>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={isSaving}>
            <Check className="h-4 w-4 mr-1" />
            Save Gallery
          </Button>
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('group relative', className)}>
      <div className="grid grid-cols-4 gap-3 border-2 border-dashed border-transparent hover:border-orange-300 transition-all rounded p-2">
        {Array.from({ length: 4 }, (_, i) => normalizedImages[i] || '').map((url, index) => {
          // If slot is empty, show placeholder
          if (!url) {
            return (
              <button
                key={index}
                onClick={() => setIsEditing(true)}
                className="relative aspect-video bg-muted/20 rounded-lg overflow-hidden border-2 border-dashed border-muted-foreground/30 hover:border-orange-400 transition-all flex items-center justify-center group/slot"
              >
                <div className="text-center">
                  <Plus className="h-8 w-8 text-muted-foreground/40 mx-auto mb-1 group-hover/slot:text-orange-500 transition-colors" />
                  <span className="text-xs text-muted-foreground/50 group-hover/slot:text-orange-500 transition-colors">
                    Add Media
                  </span>
                </div>
              </button>
            );
          }
          
          // If slot has media, render as before
          const mediaType = getMediaType(url);
          const thumbnail = mediaType === 'video' ? getYoutubeThumbnail(url) : url;
          
          return (
            <button
              key={index}
              onClick={() => onImageSelect(url)}
              className={`group relative aspect-video bg-card rounded-lg overflow-hidden border-2 transition-all shadow-sm hover:shadow-md ${
                selectedImage === url
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-border hover:border-primary'
              }`}
            >
              {mediaType === 'pdf' ? (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <FileText className="h-12 w-12 text-muted-foreground" />
                </div>
              ) : (
                <img
                  src={thumbnail || url}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    if (target.src.includes('maxresdefault')) {
                      target.src = target.src.replace('maxresdefault', 'sddefault');
                    } else if (target.src.includes('sddefault')) {
                      target.src = target.src.replace('sddefault', 'hqdefault');
                    }
                  }}
                />
              )}

              {mediaType === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <PlayCircle className="h-10 w-10 text-white drop-shadow-lg" />
                </div>
              )}
              <div
                className={`absolute inset-0 transition-colors ${
                  selectedImage === url
                    ? 'bg-black/0'
                    : 'bg-black/0 group-hover:bg-black/10'
                }`}
              />
            </button>
          );
        })}
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-orange-500 hover:bg-orange-600 text-white"
        onClick={() => setIsEditing(true)}
      >
        <Pencil className="h-3 w-3 mr-1" />
        Edit Gallery
      </Button>
    </div>
  );
};
